//
// Cytoscape.js force-directed graph for visualising academic resource
// connections. Nodes represent resources (journals, videos, courses, etc.),
// edges represent semantic similarity, citations, and AI-discovered links.
//
// Node styling by resource type (colour from RESOURCE_TYPE_COLORS):
//   journal:     star             #8B5CF6 (purple)
//   video:       hexagon          #EF4444 (red)
//   audio:       pentagon         #3B82F6 (blue)
//   image:       ellipse          #22C55E (green)
//   website:     roundrectangle   #14B8A6 (teal)
//   course:      diamond          #F59E0B (amber)
//   document:    rectangle        #6B7280 (gray)
//
// Edge styling:
//   similarity:   dashed purple
//   citation:     solid gray with arrow
//   same-author:  dotted blue
//   cross-domain: solid orange (acad-500) with arrow, labelled "AI-discovered"
//   shared-topic: solid teal
//
// Interactions: click select, hover highlight neighbourhood,
//               scroll zoom, drag pan

import React, { useRef, useCallback, useMemo, useEffect } from 'react'
// @ts-ignore -- no @types/react-cytoscapejs package available
import CytoscapeComponent from 'react-cytoscapejs'
import type cytoscape from 'cytoscape'
import { Info, Loader2, Sparkles } from 'lucide-react'
import { RESOURCE_TYPE_COLORS } from '../../constants/colors'
import type { ConnectionsNode, ConnectionsEdge, ResourceType } from '../../types'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export type LayoutType = 'cose' | 'circle' | 'grid' | 'breadthfirst'

export interface ConnectionsGraphProps {
  nodes: ConnectionsNode[]
  edges: ConnectionsEdge[]
  onNodeClick?: (node: ConnectionsNode) => void
  onExpandNode?: (nodeId: string, nodeType: string) => void
  expandedNodeIds?: Set<string>
  isExpanding?: boolean
  layout?: LayoutType
  selectedNodeId?: string
  className?: string
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const MAX_VISIBLE_NODES = 500

const NODE_TYPE_CONFIG: Record<string, { label: string; shape: string; color: string; size: number }> = {
  journal:  { label: 'Journal Article', shape: 'star',           color: RESOURCE_TYPE_COLORS.journal  || '#8B5CF6', size: 50 },
  video:    { label: 'Video',           shape: 'hexagon',        color: RESOURCE_TYPE_COLORS.video    || '#EF4444', size: 45 },
  audio:    { label: 'Audio',           shape: 'pentagon',       color: RESOURCE_TYPE_COLORS.audio    || '#3B82F6', size: 45 },
  image:    { label: 'Image',           shape: 'ellipse',        color: RESOURCE_TYPE_COLORS.image    || '#22C55E', size: 42 },
  website:  { label: 'Website',         shape: 'roundrectangle', color: RESOURCE_TYPE_COLORS.website  || '#14B8A6', size: 45 },
  course:   { label: 'Course',          shape: 'diamond',        color: RESOURCE_TYPE_COLORS.course   || '#F59E0B', size: 48 },
  document: { label: 'Document',        shape: 'rectangle',      color: RESOURCE_TYPE_COLORS.document || '#6B7280', size: 42 },
}

// ---------------------------------------------------------------------------
// Cytoscape stylesheet
// ---------------------------------------------------------------------------
const buildStylesheet = (): any[] => {
  const sheets: any[] = [
    // Base node
    {
      selector: 'node',
      style: {
        label: 'data(label)',
        'text-valign': 'bottom',
        'text-halign': 'center',
        'font-size': '9px',
        'text-wrap': 'wrap',
        'text-max-width': '100px',
        color: '#374151',
        'text-margin-y': 6,
        'background-opacity': 0.85,
        'border-width': 1.5,
      },
    },
    // Base edge
    {
      selector: 'edge',
      style: {
        width: 1.5,
        'curve-style': 'bezier',
        'target-arrow-shape': 'none',
        'arrow-scale': 0.7,
        opacity: 0.6,
      },
    },
    // Edge type: similarity -- dashed purple
    {
      selector: 'edge[type="similarity"]',
      style: {
        'line-color': '#8B5CF6',
        'line-style': 'dashed',
        width: 2,
      },
    },
    // Edge type: citation -- solid gray with arrow
    {
      selector: 'edge[type="citation"]',
      style: {
        'line-color': '#6B7280',
        'target-arrow-color': '#6B7280',
        'target-arrow-shape': 'triangle',
        'line-style': 'solid',
        width: 1.5,
      },
    },
    // Edge type: same-author -- dotted blue
    {
      selector: 'edge[type="same-author"]',
      style: {
        'line-color': '#3B82F6',
        'line-style': 'dotted',
        width: 1.5,
      },
    },
    // Edge type: cross-domain -- solid orange with arrow, labelled
    {
      selector: 'edge[type="cross-domain"]',
      style: {
        'line-color': '#EA6025',
        'target-arrow-color': '#EA6025',
        'target-arrow-shape': 'triangle',
        'line-style': 'solid',
        width: 2,
        label: 'AI-discovered',
        'font-size': '7px',
        'text-rotation': 'autorotate',
        color: '#EA6025',
      },
    },
    // Edge type: shared-topic -- solid teal
    {
      selector: 'edge[type="shared-topic"]',
      style: {
        'line-color': '#14B8A6',
        'line-style': 'solid',
        width: 1.5,
      },
    },
    // AI-discovered edges (generic)
    {
      selector: 'edge[ai_discovered="true"]',
      style: {
        'line-style': 'dashed',
        width: 2,
      },
    },
    // Selected
    {
      selector: 'node:selected',
      style: {
        'border-width': 4,
        'border-color': '#EA6025',
        'overlay-opacity': 0.15,
        'overlay-color': '#EA6025',
      },
    },
    // Hover highlight
    { selector: 'node.highlighted', style: { 'border-width': 3, 'border-color': '#EA6025' } },
    { selector: 'edge.highlighted', style: { opacity: 1, width: 3 } },
    { selector: 'node.faded', style: { opacity: 0.12 } },
    { selector: 'edge.faded', style: { opacity: 0.06 } },
    { selector: 'node:active', style: { 'overlay-color': '#EA6025', 'overlay-opacity': 0.2 } },
    // Expanded nodes: dashed border to indicate already-expanded
    {
      selector: 'node.expanded',
      style: {
        'border-style': 'dashed',
        'border-width': 3,
        'border-color': '#EA6025',
      },
    },
  ]

  // Per-type node styles
  for (const [type, config] of Object.entries(NODE_TYPE_CONFIG)) {
    sheets.push({
      selector: `node[type="${type}"]`,
      style: {
        shape: config.shape as any,
        width: config.size,
        height: config.size,
        'background-color': config.color,
        'border-color': config.color,
      },
    })
  }

  return sheets
}

const CYTOSCAPE_STYLESHEET = buildStylesheet()

// ---------------------------------------------------------------------------
// Layout configs
// ---------------------------------------------------------------------------
const LAYOUT_CONFIGS: Record<string, cytoscape.LayoutOptions> = {
  cose: {
    name: 'cose',
    animate: true,
    animationDuration: 500,
    nodeRepulsion: () => 10000,
    idealEdgeLength: () => 120,
    nodeOverlap: 20,
    gravity: 0.2,
    padding: 50,
  } as cytoscape.LayoutOptions,
  breadthfirst: {
    name: 'breadthfirst',
    directed: false,
    spacingFactor: 1.5,
    padding: 50,
    animate: true,
    animationDuration: 500,
  } as cytoscape.LayoutOptions,
  circle: {
    name: 'circle',
    padding: 50,
    animate: true,
    animationDuration: 500,
  } as cytoscape.LayoutOptions,
  grid: {
    name: 'grid',
    padding: 50,
    animate: true,
    animationDuration: 500,
    rows: 4,
  } as cytoscape.LayoutOptions,
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const EXPANDABLE_TYPES = new Set(['journal', 'video', 'audio', 'image', 'website', 'course', 'document'])

const ConnectionsGraph: React.FC<ConnectionsGraphProps> = ({
  nodes,
  edges,
  onNodeClick,
  onExpandNode,
  expandedNodeIds,
  isExpanding = false,
  layout = 'cose',
  selectedNodeId,
  className = '',
}) => {
  const cyRef = useRef<cytoscape.Core | null>(null)

  // Limit visible nodes
  const filteredNodes = useMemo(() => nodes.slice(0, MAX_VISIBLE_NODES), [nodes])
  const nodeIdSet = useMemo(() => new Set(filteredNodes.map(n => n.id)), [filteredNodes])
  const filteredEdges = useMemo(
    () => edges.filter(e => nodeIdSet.has(e.source) && nodeIdSet.has(e.target)),
    [edges, nodeIdSet]
  )

  // Build Cytoscape element array
  const elements = useMemo(() => [
    ...filteredNodes.map(node => ({
      data: {
        id: node.id,
        label: node.label.length > 40 ? node.label.slice(0, 37) + '...' : node.label,
        fullLabel: node.label,
        type: node.type,
        score: node.score || 0,
      },
    })),
    ...filteredEdges.map((edge, i) => ({
      data: {
        id: `edge-${edge.source}-${edge.target}-${i}`,
        source: edge.source,
        target: edge.target,
        type: edge.type,
        weight: edge.weight || 0.5,
        ai_discovered: edge.ai_discovered ? 'true' : 'false',
      },
    })),
  ], [filteredNodes, filteredEdges])

  const handleCyReady = useCallback((cy: cytoscape.Core) => {
    cyRef.current = cy

    // Click: select node + trigger expand
    cy.on('tap', 'node', (evt) => {
      const d = evt.target.data()
      const nodeObj: ConnectionsNode = {
        id: d.id,
        label: d.fullLabel || d.label,
        type: d.type as ResourceType,
        score: d.score,
      }
      if (onNodeClick) onNodeClick(nodeObj)
      // Auto-expand on click if the node type is expandable
      if (onExpandNode && EXPANDABLE_TYPES.has(d.type)) {
        onExpandNode(d.id, d.type)
      }
    })

    // Hover: highlight neighbourhood
    cy.on('mouseover', 'node', (evt) => {
      const node = evt.target
      const neighbourhood = node.closedNeighborhood()
      cy.elements().addClass('faded')
      neighbourhood.removeClass('faded').addClass('highlighted')
    })

    cy.on('mouseout', 'node', () => {
      cy.elements().removeClass('faded').removeClass('highlighted')
    })

    // Centre on selected node if specified
    if (selectedNodeId) {
      const selected = cy.getElementById(selectedNodeId)
      if (selected.length) {
        selected.select()
        cy.animate({ center: { eles: selected }, zoom: 1.5, duration: 300 })
      }
    }
  }, [onNodeClick, onExpandNode, selectedNodeId])

  // Mark expanded nodes with dashed border class
  useEffect(() => {
    const cy = cyRef.current
    if (!cy || !expandedNodeIds) return
    cy.nodes().forEach((node) => {
      if (expandedNodeIds.has(node.id())) {
        node.addClass('expanded')
      } else {
        node.removeClass('expanded')
      }
    })
  }, [expandedNodeIds, nodes])

  const layoutConfig = LAYOUT_CONFIGS[layout] || LAYOUT_CONFIGS.cose

  return (
    <div className={`relative w-full h-full ${className}`} style={{ minHeight: '500px' }}>
      {/* Performance cap warning */}
      {nodes.length > MAX_VISIBLE_NODES && (
        <div className="absolute top-2 left-2 z-20 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 text-xs text-amber-700">
          Showing {MAX_VISIBLE_NODES} of {nodes.length} nodes
        </div>
      )}

      {/* Graph canvas */}
      {elements.length > 0 ? (
        <>
          <CytoscapeComponent
            elements={elements}
            stylesheet={CYTOSCAPE_STYLESHEET}
            layout={layoutConfig}
            cy={(cy: cytoscape.Core) => handleCyReady(cy)}
            {...{ style: { width: '100%', height: '100%', minHeight: '500px' } } as any}
            className="w-full h-full"
            minZoom={0.15}
            maxZoom={3}
            wheelSensitivity={0.3}
          />

          {/* Find 10 More button at bottom of graph */}
          {onExpandNode && selectedNodeId && EXPANDABLE_TYPES.has(
            nodes.find(n => n.id === selectedNodeId)?.type || ''
          ) && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20">
              <button
                onClick={() => {
                  const node = nodes.find(n => n.id === selectedNodeId)
                  if (node) onExpandNode(node.id, node.type)
                }}
                disabled={isExpanding || expandedNodeIds?.has(selectedNodeId)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white
                           bg-acad-500 rounded-full shadow-lg hover:bg-acad-600 transition-colors
                           disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isExpanding ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                {expandedNodeIds?.has(selectedNodeId) ? 'Already Expanded' : 'Find 10 More'}
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <Info className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No connections to display</p>
            <p className="text-xs text-gray-400 mt-1">Search for a resource to explore its connections</p>
          </div>
        </div>
      )}
    </div>
  )
}

export { NODE_TYPE_CONFIG }
export default ConnectionsGraph
